import { SideModal } from "shared";
import SVG from "shared/SVG";
import { SVG_ENUM } from "enums";
import { useState } from "react";
import "./OtherAssetDetailsSideModal.scss";

interface OtherAssetDetailsSideModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
  investmentTypeName?: string;
}

const OtherAssetDetailsSideModal = ({
  isOpen,
  onClose,
  item,
  investmentTypeName,
}: OtherAssetDetailsSideModalProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const photoFiles = (item?.files ?? []).filter((f: any) => f.role === "photo");

  const allFiles = (item?.files ?? []).filter(
    (f: any) => f.role === "contract" || f.role === "attachment",
  );

  const handleDownloadAll = () => {
    if (!item) return;
    allFiles.forEach((file: any) => {
      const link = document.createElement("a");
      link.href = file.file.url;
      link.download = file.file.name;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === 0 ? photoFiles.length - 1 : prev - 1,
    );
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev === photoFiles.length - 1 ? 0 : prev + 1,
    );
  };

  const formatPrice = () => {
    if (!item?.primaryValue) return "Brak";
    const formatted = Number(item.primaryValue).toLocaleString("pl-PL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const currency = item.currency?.currency ?? "";
    return currency ? `${formatted} ${currency}` : formatted;
  };

  const subtitle = investmentTypeName
    ? `Aktywo: ${investmentTypeName}`
    : "Szczegółowe informacje";

  return (
    <SideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Szczegółowe informacje"
      subtitle={subtitle}
      className="side-modal--other-asset-details"
    >
      <div className="other-asset-details">
        <div className="other-asset-details__price-header">
          Cena zakupu: <span>{formatPrice()}</span>
        </div>

        <div className="other-asset-details__field">
          <span className="other-asset-details__label">Nazwa</span>
          <span className="other-asset-details__value">
            {item?.name || "Brak"}
          </span>
        </div>

        <div className="other-asset-details__field">
          <span className="other-asset-details__label">Waluta</span>
          <span className="other-asset-details__value">
            {item?.currency?.currency || "Brak"}
          </span>
        </div>

        <div className="other-asset-details__field">
          <span className="other-asset-details__label">
            Dodatkowe informacje
          </span>
          <span className="other-asset-details__value">
            {item?.additionalInfo || "Brak"}
          </span>
        </div>

        <div className="other-asset-details__field other-asset-details__field--photos">
          <span className="other-asset-details__label">
            Załączone zdjęcia ({photoFiles.length})
          </span>
          {photoFiles.length > 0 ? (
            <div className="other-asset-details__carousel">
              <div className="other-asset-details__carousel-image-wrapper">
                <img
                  src={photoFiles[currentPhotoIndex]?.file?.url}
                  alt={`Zdjęcie ${currentPhotoIndex + 1}`}
                  className="other-asset-details__carousel-image"
                />

                {photoFiles.length > 1 && (
                  <>
                    <button
                      className="other-asset-details__carousel-arrow other-asset-details__carousel-arrow--prev"
                      onClick={handlePrevPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      className="other-asset-details__carousel-arrow other-asset-details__carousel-arrow--next"
                      onClick={handleNextPhoto}
                    >
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.87695 0.779297L0.778561 3.91127L3.87695 7.04325"
                          stroke="#151515"
                          strokeWidth="1.55757"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {photoFiles.length > 1 && (
                <div className="other-asset-details__carousel-dots">
                  {photoFiles.map((_: any, index: number) => (
                    <button
                      key={index}
                      className={`other-asset-details__carousel-dot ${
                        index === currentPhotoIndex
                          ? "other-asset-details__carousel-dot--active"
                          : ""
                      }`}
                      onClick={() => setCurrentPhotoIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="other-asset-details__value">Brak</span>
          )}
        </div>

        <div className="other-asset-details__field other-asset-details__field--files">
          <div className="other-asset-details__files-header">
            <span className="other-asset-details__label">
              Załączone pliki ({allFiles.length})
            </span>
            {allFiles.length > 0 && (
              <button
                className="other-asset-details__download-all"
                onClick={handleDownloadAll}
              >
                Pobierz wszystko
                <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
              </button>
            )}
          </div>
          {allFiles.length === 0 ? (
            <span className="other-asset-details__value">Brak</span>
          ) : (
            <div className="other-asset-details__files-list">
              {allFiles.map((file: any) => (
                <div
                  key={file.file.uuid}
                  className="other-asset-details__file-item"
                >
                  <span className="other-asset-details__file-name">
                    {file.file.name}
                  </span>
                  <a
                    href={file.file.url}
                    download={file.file.name}
                    target="_blank"
                    rel="noreferrer"
                    className="other-asset-details__file-download"
                  >
                    <SVG type={SVG_ENUM.FILE_DOWNLOAD} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default OtherAssetDetailsSideModal;
